/* eslint-disable @typescript-eslint/no-non-null-assertion */
import {
  Box,
  Button,
  CircularProgress,
  IconButton,
  InputAdornment,
} from "@mui/material";
import { useMutation } from "@tanstack/react-query";
import { GKTextField } from "components";
import useToast from "hooks/useToast";
import { useAppDispatch } from "hooks/useToolkit";
import { useState } from "react";
import { FiEye, FiEyeOff } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import authService from "services/auth";
import { AuthenticationActions } from "services/redux/slices/Authentication";
import LogImg from "../../assets/login.png";

function LoginComponent() {
  const [loginCredentials, setLoginCredentials] = useState({
    phone: "",
    password: "",
  });
  const [showPassword, setShowPassword] = useState(false);
  const toast = useToast();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { mutate: handleLogin, isLoading: isSubmitting } = useMutation({
    mutationFn: () => authService.login(loginCredentials),
    onSuccess: (response) => {
      dispatch(AuthenticationActions.Login(response.data!));
      navigate("/");
      window.location.reload();
    },
    onError: (error: any) => {
      if (error?.response?.status === 400) {
        toast.show("error", "Invalid phone or password, Please try again");
      } else if (error?.response?.data?.isAdmin === false) {
        toast.show("error", `You don't have access to this domain`);
      } else {
        toast.show("error", "Something went wrong, Please try again later");
      }
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    handleLogin();
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        width: "100vw",
        position: "relative",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        p: 2,
        overflow: "hidden",
      }}
    >
      <img
        src={LogImg}
        alt=""
        style={{
          objectFit: "cover",
          height: "100%",
          width: "100%",
          position: "absolute",
          inset: 0,
          zIndex: 0,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          inset: 0,
          zIndex: 1,
          backdropFilter: "blur(10px)",
        }}
      />

      <Box
        component="form"
        onSubmit={handleSubmit}
        sx={{
          position: "relative",
          zIndex: 2,
          width: "100%",
          maxWidth: 420,
          bgcolor: "rgba(255, 255, 255, 0.85)",
          borderRadius: "12px",
          boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
          p: { xs: 3, sm: 5 },
          display: "flex",
          flexDirection: "column",
          gap: 2.5,
        }}
      >
        <Box textAlign="center">
          <img
            src="/Logos/Gridkey_BrandGuidelines-removebg-preview.png"
            alt="GridKey Logo"
            style={{ height: 56, maxWidth: "100%", objectFit: "contain" }}
          />
        </Box>

        <GKTextField
          inputLabel="Phone"
          placeholder="Enter phone number"
          type="tel"
          required
          autoFocus
          autoComplete="username"
          value={loginCredentials.phone}
          slotProps={{ htmlInput: { inputMode: "numeric", maxLength: 10 } }}
          onChange={(e) =>
            setLoginCredentials({
              ...loginCredentials,
              phone: e.target.value.replace(/\D/g, ""),
            })
          }
        />

        <GKTextField
          inputLabel="Password"
          placeholder="Enter password"
          type={showPassword ? "text" : "password"}
          required
          autoComplete="current-password"
          value={loginCredentials.password}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    size="small"
                    edge="end"
                    aria-label={
                      showPassword ? "Hide password" : "Show password"
                    }
                    onClick={() => setShowPassword((prev) => !prev)}
                  >
                    {showPassword ? (
                      <FiEyeOff size={18} />
                    ) : (
                      <FiEye size={18} />
                    )}
                  </IconButton>
                </InputAdornment>
              ),
            },
          }}
          onChange={(e) =>
            setLoginCredentials({
              ...loginCredentials,
              password: e.target.value,
            })
          }
        />

        <Button
          fullWidth
          size="large"
          variant="contained"
          disabled={isSubmitting}
          type="submit"
          startIcon={
            isSubmitting ? <CircularProgress size={16} color="inherit" /> : null
          }
        >
          {isSubmitting ? "Signing in..." : "Sign in"}
        </Button>
      </Box>
    </Box>
  );
}

export default LoginComponent;
